import React from "react";
import { Box, Typography, Switch, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { debounce } from "lodash";
import GetUserOnline from "src/graphql/mutation/chat/getUserOnline";
import { useMutation } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import { useSelector, useDispatch } from "react-redux";
import { meHandler } from "src/store/reducers/meSlice";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 40,
  height: 22,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 17,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(11px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(18px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#10b981",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 18,
    height: 18,
    borderRadius: 10,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

const GlobalAvailabilityToggle = () => {
  const { me }: any = useSelector((state: any) => state.me);
  const status = me?.onlineStatus;
  const dispatch = useDispatch();

  const [getUserOnline, { loading: getUserOnlineLoading }] =
    useMutation(GetUserOnline);

  const debouncedHandleChange = debounce(async (checked: boolean) => {
    try {
      const result = await getUserOnline({
        variables: {
          status: checked,
        },
      });

      if (result.data?.getUserOnline?.success) {
        dispatch(
          meHandler({
            ...me,
            onlineStatus: checked,
          }),
        );
      }
    } catch (error: any) {
      enqueueSnackbar("Something went wrong! Try again.", {
        variant: "error",
      });
    }
  }, 300);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedHandleChange(event.target.checked);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        mr: 2,
        padding: "6px 12px",
        borderRadius: "20px",
        backgroundColor: "rgba(248, 250, 252, 0.8)",
        border: "1px solid #e2e8f0",
        transition: "all 0.3s ease",
        opacity: getUserOnlineLoading ? 0.7 : 1,
        pointerEvents: getUserOnlineLoading ? "none" : "auto",
        "&:hover": {
          backgroundColor: "#f1f5f9",
          borderColor: "#cbd5e1",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {getUserOnlineLoading ? (
          <CircularProgress
            size={10}
            sx={{
              color: status ? "#10b981" : "#94a3b8",
              thickness: 6,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: status ? "#10b981" : "#94a3b8",
              boxShadow: status ? "0 0 0 4px rgba(16, 185, 129, 0.15)" : "none",
              transition: "all 0.3s ease",
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            color: status ? "#1e293b" : "#64748b",
            fontFamily: "'Outfit', sans-serif",
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
            transition: "all 0.3s ease",
          }}
        >
          {status ? "Accepting Chats" : "Offline"}
        </Typography>
      </Box>
      <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
        <AntSwitch checked={!!status} onChange={handleChange} />
      </Box>
    </Box>
  );
};

export default GlobalAvailabilityToggle;
